import { useMemo, useState } from 'react';
import { useGetAlarmsQuery } from 'services/Alarms/Alarms.api';
import AlarmsTable from '../../components/AlarmsTable';
import ShareAlarmModal from '../../components/ShareAlarmModal';
import type { Alarm } from '../../types/Alarms.types';
import { buildAlarmQueryParams } from '../../utils/AlarmQueryBuilder';
import AlarmsHeader from 'features/Alarms/components/AlarmsHeader/AlarmsHeader';
import AlarmsAccordion from 'features/Alarms/components/AlarmsAccordion/AlarmsAccordion';

export default function AlarmHistory() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareAlarmData, setShareAlarmData] = useState<Alarm | null>(null);

  const params = useMemo(
    () => buildAlarmQueryParams(page + 1, limit, 0, search), // state 0 for historical alarms
    [page, limit, search],
  );
  const { data: alarmQuery, isLoading, isFetching, error } = useGetAlarmsQuery(params);
  const alarmList = alarmQuery?.data || [];

  // Calculate max page index (zero-based)
  const totalCount = alarmQuery?.meta?.resultCount || 0;
  const maxPageIndex = Math.max(0, Math.ceil(totalCount / limit) - 1);

  // Handler for page change (zero-based)
  const handlePageChanged = (newPage: number) => {
    setPage(newPage);
  };
  const handleLimitChanged = (newLimit: number) => {
    setLimit(newLimit);
    setPage(0); // reset to first page
  };
  const handleSearchChanged = (newSearch: string) => {
    setSearch(newSearch);
    setPage(0); // reset to first page
  };

  // Bulk select logic (no actions)
  const allSelected = alarmList?.length > 0 && selectedRowIds.length === alarmList.length;
  const onSelectAll = () => {
    if (!alarmList?.length) return;
    if (selectedRowIds.length !== alarmList.length) {
      setSelectedRowIds(alarmList.map((alarm: Alarm) => alarm.id));
    } else {
      setSelectedRowIds([]);
    }
  };

  // Only sharing is enabled
  const onShare = (alarm: Alarm) => {
    setShareAlarmData(alarm);
    setShareOpen(true);
  };

  return (
    <div className="d-flex flex-column h-100">
      <AlarmsHeader
        title="Alarm History"
        pageIndex={page}
        maxPageIndex={maxPageIndex}
        headerFields={[]}
        onPageChanged={handlePageChanged}
        limit={limit}
        onLimitChanged={handleLimitChanged}
        onSearchChanged={handleSearchChanged}
        silenceControlsEnabled={false}
        allSelected={allSelected}
        onSelectAll={onSelectAll}
      />
      <div className="flex-grow-1 overflow-auto">
        {isLoading || isFetching ? (
          <div className="text-center py-5 text-secondary">Loading alarms…</div>
        ) : error ? (
          <div className="text-center py-5 text-danger">Failed to load alarms.</div>
        ) : !alarmList || alarmList.length === 0 ? (
          <div className="text-center py-5 text-muted">No alarms found.</div>
        ) : (
          <>
            <div className="d-none d-lg-block">
              <AlarmsTable
                alarms={alarmList}
                selectedRowIds={selectedRowIds}
                setSelectedRowIds={setSelectedRowIds}
                handleShare={onShare}
              />
            </div>
            <div className="d-block d-lg-none">
              <AlarmsAccordion
                alarms={alarmList}
                selectedRowIds={selectedRowIds}
                setSelectedRowIds={setSelectedRowIds}
                handleShare={onShare}
              />
            </div>
          </>
        )}
      </div>
      <ShareAlarmModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setShareAlarmData(null);
        }}
        alarm={shareAlarmData}
      />
    </div>
  );
}
